import { useEffect, useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useGlobalOrder } from "../../utils/GlobalStateContext";
import { getTrashReminderSchedule } from "./trashReminderSchedule";
import "./TrashReminder.css";

const COMPLETED_DAY_KEY = "kitchen_trash_completed_date";
const SHOWN_DAY_KEY = "kitchen_trash_shown_date";
// beep.mp3 約 1 秒，每秒播一次共 5 次 ≈ 響 5 秒
const BEEP_TIMES = 5;

function TrashMascot() {
  return (
    <svg className="trash-mascot" viewBox="0 0 100 112" aria-hidden="true" focusable="false">
      <ellipse cx="50" cy="104" rx="28" ry="4" fill="#CCE8DE" />
      <g className="trash-mascot-body">
        <path d="M29 70 Q15 68 16 57" fill="none" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
        <g className="trash-mascot-wave">
          <path d="M73 65 Q88 60 85 45" fill="none" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
          <path d="M85 46 L80 39 M85 46 L88 37" fill="none" stroke="#31846C" strokeWidth="3" strokeLinecap="round" />
        </g>
        <path d="M38 96 L35 101 M63 96 L66 101" stroke="#31846C" strokeWidth="5" strokeLinecap="round" />
        <path d="M26 39 H75 L70 88 Q69 97 60 97 H41 Q32 97 31 88 Z" fill="#80CFB1" stroke="#31846C" strokeWidth="3" />
        <path d="M35 45 L39 86" stroke="#B9E8D5" strokeWidth="4" strokeLinecap="round" />
        <g className="trash-mascot-lid">
          <rect x="23" y="32" width="55" height="9" rx="4.5" fill="#31846C" />
          <path d="M42 31 V27 Q42 24 46 24 H55 Q59 24 59 27 V31" fill="none" stroke="#31846C" strokeWidth="4" />
        </g>
        <circle cx="42" cy="62" r="3" fill="#215846" />
        <circle cx="60" cy="62" r="3" fill="#215846" />
        <ellipse cx="36" cy="69" rx="5" ry="3" fill="#F5B7AB" />
        <ellipse cx="66" cy="69" rx="5" ry="3" fill="#F5B7AB" />
        <path d="M46 70 Q51 76 56 70" fill="none" stroke="#215846" strokeWidth="2.5" strokeLinecap="round" />
      </g>
      <g className="trash-mascot-sparkles" fill="#E7B44C">
        <path d="M18 23 L20 17 L22 23 L28 25 L22 27 L20 33 L18 27 L12 25 Z" />
        <path d="M80 16 L82 12 L84 16 L88 18 L84 20 L82 24 L80 20 L76 18 Z" />
      </g>
    </svg>
  );
}

export default function TrashReminder() {
  const { systemData, playBeep } = useGlobalOrder();
  const enabled = [2, 3].includes(Number(systemData?.position_id)) &&
    systemData?.client_mode !== "kiosk";
  const [reminderDay, setReminderDay] = useState(null);
  const completedDay = useRef(null);
  const shownDay = useRef(null);

  useEffect(() => {
    if (!enabled) {
      setReminderDay(null);
      return undefined;
    }

    let timer;
    const check = () => {
      const schedule = getTrashReminderSchedule();
      const day = schedule?.day;
      let savedDay = null;
      let savedShownDay = null;
      try {
        savedDay = localStorage.getItem(COMPLETED_DAY_KEY);
        savedShownDay = localStorage.getItem(SHOWN_DAY_KEY);
      } catch {
        // 儲存不可用時，仍可在這次開啟期間關閉提醒。
      }
      if (!day || day === savedDay || day === completedDay.current) {
        setReminderDay(null);
        return;
      }
      if (schedule.shouldStart && document.visibilityState !== "hidden" &&
          day !== savedShownDay && day !== shownDay.current) {
        shownDay.current = day;
        try {
          localStorage.setItem(SHOWN_DAY_KEY, day);
        } catch {
          // 儲存不可用時，這次開啟期間仍只觸發一次。
        }
        setReminderDay(day);
      }
    };
    const tick = () => {
      check();
      // 對齊下一個整分鐘，準時在 14:15 檢查，也處理跨日。
      timer = setTimeout(tick, 60000 - (Date.now() % 60000));
    };
    tick();
    window.addEventListener("storage", check);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("storage", check);
    };
  }, [enabled]);

  // 提醒跳出時響 5 秒；用自己的計時器呼叫 playBeep，不動到新訂單提醒共用的 startBeep/stopBeep
  useEffect(() => {
    if (!reminderDay || !playBeep) return undefined;
    playBeep();
    let played = 1;
    const interval = setInterval(() => {
      playBeep();
      played += 1;
      if (played >= BEEP_TIMES) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [reminderDay, playBeep]);

  const complete = () => {
    completedDay.current = reminderDay;
    try {
      localStorage.setItem(COMPLETED_DAY_KEY, reminderDay);
    } catch {
      // 不讓瀏覽器的儲存限制擋住完成按鈕。
    }
    setReminderDay(null);
  };

  if (!enabled || !reminderDay) return null;

  return (
    <Box className="trash-reminder" sx={{ zIndex: (theme) => theme.zIndex.appBar + 1 }}>
      <TrashMascot />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box role="status" aria-live="polite" aria-atomic="true">
          <Typography sx={{ color: "#56806E", fontSize: 12, fontWeight: 800, letterSpacing: 1 }}>
            14:15 · 倒垃圾提醒
          </Typography>
          <Typography sx={{ color: "#215846", fontSize: 22, fontWeight: 900, mt: 0.25 }}>
            該倒垃圾囉！
          </Typography>
          <Typography sx={{ color: "#56806E", fontSize: 13, mt: 0.25 }}>
            垃圾車 14:30 離開，記得帶上垃圾
          </Typography>
        </Box>
        <Button
          onClick={complete}
          startIcon={<CheckRoundedIcon />}
          sx={{
            mt: 1,
            minHeight: 44,
            px: 2,
            borderRadius: "14px",
            backgroundColor: "#31846C",
            color: "#FFFFFF",
            fontSize: 15,
            fontWeight: 800,
            "&:hover": { backgroundColor: "#266C58" },
          }}
        >
          已倒垃圾
        </Button>
      </Box>
    </Box>
  );
}
